import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { contactAdmin } from '@/services/userAlerts'
import { convertToMiliseconds } from '@/services/functions'
import {
  LOCAL_KEYS,
  SESSION_KEYS,
  getParsed,
  setWithExpiry,
  getWithExpiry
} from '@/services/browserStorage'
import { getClientLanguage } from '@/services/language'
import { wasContentRecentlyCreated } from '@/utils/new-content'
import {
  CacheNavData,
  Case,
  ContentItem,
  ContentItemWithModuleId,
  Tree,
  TreeItem
} from '@/types/contentTree'

import { ENDPOINTS, sendGetRequest, handleResponseStates } from '@/services/api'

interface ContentTreeContextType {
  hasTree: boolean
  tree: Tree
  cachedNavData: CacheNavData
  loadingTree: boolean
  contentsPerId: Record<string, ContentItemWithModuleId>
  modulesPerId: Record<number, TreeItem>
  getContentsList: (
    openModulesOnly?: boolean,
    openContentsOnly?: boolean
  ) => ContentItemWithModuleId[]
  setCachedNavData: React.Dispatch<React.SetStateAction<CacheNavData>>
  newestContent: ContentItem[]
  cases: Case[]
}

export const ContentTreeContext = createContext<ContentTreeContextType>(
  {} as ContentTreeContextType
)

interface ContentTreeProviderProps {
  children: React.ReactNode
}

export function ContentTreeProvider({ children }: ContentTreeProviderProps) {
  const { user } = useAuth()
  const [tree, setTree] = useState<Tree>([])
  const [loadingTree, setLoadingTree] = useState<boolean>(true)
  const [cachedNavData, setCachedNavData] = useState<CacheNavData>(
    {} as CacheNavData
  )
  const [newestContent, setNewestContent] = useState<ContentItem[]>([])
  const [cases, setCases] = useState<Case[]>([])

  const didMount = useRef<boolean>(false)

  const applyAllowedAccess = (modules: Tree, allowedModules: number[]) => {
    const set: Set<string | number> = new Set(
      Array.isArray(allowedModules) ? allowedModules : []
    )
    return modules.map(m => {
      // banco novo o id será uuid, gerar cache da tree para corrigir, ou alterar get-my-infos p/ retornar o id_v1
      const allowedAccess = set.size > 0 && (set.has(m.id) || set.has(m.id_v1))
      const subCat = m.subCat.map(c => ({ ...c, allowedAccess }))
      return { ...m, allowedAccess, subCat }
    })
  }

  const assignModuleIndexes = (arr: Tree) => {
    arr.forEach((module, i) => {
      module.originalIndex = i
    })
  }

  const fixNewContentFlags = (arr: Tree) => {
    arr.forEach(module => {
      module.new_content = module.subCat.some(wasContentRecentlyCreated)
    })
  }

  const getThreeNewestContent = (currentTree: Tree) => {
    const result = []
    currentTree.forEach(mod => {
      mod.subCat.forEach(sub => {
        if (wasContentRecentlyCreated(sub)) {
          result.push({
            ...sub,
            moduleName: mod.name,
            timestamp: new Date(sub.created_at).getTime()
          })
        }
      })
    })
    return result.sort((a, b) =>
      a.timestamp > b.timestamp ? 1 : a.timestamp < b.timestamp ? -1 : 0
    )
  }

  const onTreeLoadedFromCdn = (jsonTree: Tree, jsonCases: Case[]) => {
    // aplica allowedAccess (módulo + conteúdo)
    let decorated = applyAllowedAccess(jsonTree, user?.allowedModules)

    // ordena liberados primeiro
    decorated = decorated.sort(
      (a, b) => (b.allowedAccess ? 1 : 0) - (a.allowedAccess ? 1 : 0)
    )

    assignModuleIndexes(decorated)
    fixNewContentFlags(decorated)
    setWithExpiry(LOCAL_KEYS.content_tree, decorated, convertToMiliseconds(30))
    setWithExpiry(
      LOCAL_KEYS.content_tree_cases,
      jsonCases,
      convertToMiliseconds(30)
    )
    setTree(decorated)
    setCases(jsonCases)
    setNewestContent(getThreeNewestContent(decorated))
    setLoadingTree(false)
  }

  const responsesLoad = [
    {
      name: 200,
      callback: (response: any) => {
        const data = response?.data ?? response

        // get-tree -> { status, hash, msg: [...tree], cases: [...] }
        const jsonTree: Tree = data?.msg
        const jsonCases: Case[] = Array.isArray(data?.cases) ? data.cases : []

        if (!Array.isArray(jsonTree)) {
          console.error('get_tree payload inesperado:', data)
          contactAdmin(() => null)
          setLoadingTree(false)
          return
        }

        onTreeLoadedFromCdn(jsonTree, jsonCases)
      }
    }
  ]

  const loadTree = async () => {
    const cachedTree = getWithExpiry<Tree>(LOCAL_KEYS.content_tree)
    if (cachedTree) {
      const cachedCases = getWithExpiry<Case[]>(LOCAL_KEYS.content_tree_cases)
      fixNewContentFlags(cachedTree)
      setTree(cachedTree)
      setCases(cachedCases ?? [])
      setNewestContent(getThreeNewestContent(cachedTree))
      setLoadingTree(false)
      return
    }

    try {
      const lang = getClientLanguage()
      await sendGetRequest(ENDPOINTS.get_tree, {
        'Accept-Language': lang
      })
        .then(response => {
          handleResponseStates(response, responsesLoad)
        })
        .catch(error => {
          const msg = error.data || error
          console.error('getTree error: ', msg)
          contactAdmin(() => null)
          setLoadingTree(false)
        })
    } catch (err) {
      console.error('getTree error:', err)
      contactAdmin()
      setLoadingTree(false)
    }
  }

  useEffect(() => {
    if (didMount.current) return
    const data = getParsed<CacheNavData>(
      sessionStorage,
      SESSION_KEYS.navigation_data
    )
    setCachedNavData(data)
    didMount.current = true
  }, [])

  useEffect(() => {
    if (cachedNavData !== null && Object.keys(cachedNavData).length === 0)
      return
    loadTree()
  }, [cachedNavData])

  const getContentsList = (
    openModulesOnly = false,
    openContentsOnly = false
  ): ContentItemWithModuleId[] => {
    let modules = tree
    if (openModulesOnly) modules = modules.filter(m => m.allowedAccess)
    const flat = modules.flatMap(mod =>
      mod.subCat.map(
        item =>
          ({
            ...item,
            moduleId: mod.id,
            moduleName: mod.name
          }) as ContentItemWithModuleId
      )
    )
    return openContentsOnly ? flat.filter(c => c.allowedAccess) : flat
  }

  const contentsPerId: Record<string, ContentItemWithModuleId> = useMemo(() => {
    const contents: Record<string, ContentItemWithModuleId> = {}

    tree.forEach(mod => {
      mod.subCat.forEach(content => {
        contents[content.id] = {
          ...content,
          moduleId: mod.id,
          moduleName: mod.name
        } as ContentItemWithModuleId
      })
    })

    return contents
  }, [tree])

  const modulesPerId: Record<number, TreeItem> = useMemo(() => {
    const modules: Record<number, TreeItem> = {}

    tree.forEach(mod => {
      modules[mod.id] = mod
    })

    return modules
  }, [tree])

  return (
    <ContentTreeContext.Provider
      value={{
        hasTree: tree.length > 0,
        tree,
        cachedNavData,
        loadingTree,
        contentsPerId,
        modulesPerId,
        getContentsList,
        setCachedNavData,
        newestContent,
        cases
      }}
    >
      {children}
    </ContentTreeContext.Provider>
  )
}

export function useContentTree() {
  return useContext(ContentTreeContext)
}
